import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Stethoscope, Loader2, Mail, Lock, User, BriefcaseMedical, MapPin, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { t } from '../i18n';

export default function DoctorRegister() {
  const nav = useNavigate();
  const lang = localStorage.getItem('hb_lang') || 'en';
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    specialty: '',
    city: '',
    phone: '',
    qualification: '',
    registration_number: '',
    medical_council: '',
    clinic: '',
    registration_code: '',
  });
  const [loading, setLoading] = useState(false);

  const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/doctor/auth/register', form);
      localStorage.setItem('hb_token', data.token);
      localStorage.setItem('hb_doctor', JSON.stringify(data.doctor));
      toast.success('Application submitted. Verification is pending.');
      nav('/doctor/dashboard');
    } catch (e) {
      const msg = e?.response?.data?.detail || t(lang, 'could_not_register');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16 fade-up">
      <Card className="p-8 rounded-3xl border border-[#E8E1D5] bg-white hb-shadow-lg">
        <div className="text-center">
          <div className="h-14 w-14 rounded-2xl bg-[#1E3F33] grid place-items-center mx-auto">
            <Stethoscope className="h-7 w-7 text-white" />
          </div>
          <h1 className="font-display text-3xl text-[#1A1A1A] mt-4">{t(lang,'doctor_enroll_title')}</h1>
          <p className="text-[#4A4A4A] text-sm mt-2">Doctor access is restricted. Submit your medical registration details for verification.</p>
        </div>

        {/* Portal Switcher Toggle */}
        <div className="flex rounded-full bg-[#F4F1EA] p-1 mt-6 border border-[#E8E1D5]">
          <button
            type="button"
            onClick={() => nav('/register')}
            className="flex-1 py-2 text-xs font-semibold rounded-full text-[#7A7A7A] hover:text-[#1E3F33] transition-all"
          >
            Patient Access
          </button>
          <button
            type="button"
            className="flex-1 py-2 text-xs font-semibold rounded-full transition-all bg-[#1E3F33] text-white shadow-sm"
          >
            Doctor Access
          </button>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label className="font-semibold text-[#1E3F33]">{t(lang,'full_name')}</Label>
            <div className="relative mt-1.5">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
              <Input value={form.name} onChange={(e) => onChange('name', e.target.value)} required className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
            </div>
          </div>

          <div>
            <Label className="font-semibold text-[#1E3F33]">{t(lang,'email')}</Label>
            <div className="relative mt-1.5">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
              <Input type="email" value={form.email} onChange={(e) => onChange('email', e.target.value)} required className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
            </div>
          </div>

          <div>
            <Label className="font-semibold text-[#1E3F33]">{t(lang,'password')}</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
              <Input type="password" value={form.password} onChange={(e) => onChange('password', e.target.value)} required className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
            </div>
          </div>

          <div>
            <Label className="font-semibold text-[#1E3F33]">{t(lang,'specialty')}</Label>
            <div className="relative mt-1.5">
              <BriefcaseMedical className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
              <Input value={form.specialty} onChange={(e) => onChange('specialty', e.target.value)} required className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
            </div>
          </div>

          <div>
            <Label className="font-semibold text-[#1E3F33]">Qualification</Label>
            <div className="relative mt-1.5">
              <BriefcaseMedical className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
              <Input value={form.qualification} onChange={(e) => onChange('qualification', e.target.value)} placeholder="MBBS, MD, BAMS..." required className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
            </div>
          </div>

          <div>
            <Label className="font-semibold text-[#1E3F33]">Medical registration number</Label>
            <div className="relative mt-1.5">
              <BriefcaseMedical className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
              <Input value={form.registration_number} onChange={(e) => onChange('registration_number', e.target.value)} required className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
            </div>
          </div>

          <div>
            <Label className="font-semibold text-[#1E3F33]">Medical council</Label>
            <div className="relative mt-1.5">
              <BriefcaseMedical className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
              <Input value={form.medical_council} onChange={(e) => onChange('medical_council', e.target.value)} placeholder="State Medical Council" required className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
            </div>
          </div>

          <div>
            <Label className="font-semibold text-[#1E3F33]">{t(lang,'city')}</Label>
            <div className="relative mt-1.5">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
              <Input value={form.city} onChange={(e) => onChange('city', e.target.value)} required className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
            </div>
          </div>

          <div>
            <Label className="font-semibold text-[#1E3F33]">Clinic / hospital</Label>
            <div className="relative mt-1.5">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
              <Input value={form.clinic} onChange={(e) => onChange('clinic', e.target.value)} className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
            </div>
          </div>

          <div>
            <Label className="font-semibold text-[#1E3F33]">Doctor access code</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
              <Input value={form.registration_code} onChange={(e) => onChange('registration_code', e.target.value)} required className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
            </div>
            <p className="text-xs text-[#7A7A7A] mt-1.5">Only approved doctor applicants receive this code.</p>
          </div>

          <div>
            <Label className="font-semibold text-[#1E3F33]">{t(lang,'phone_optional')}</Label>
            <div className="relative mt-1.5">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
              <Input value={form.phone} onChange={(e) => onChange('phone', e.target.value)} className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Submit for verification
          </Button>
        </form>

        <div className="mt-4 text-center text-sm text-[#7A7A7A]">
          {t(lang,'already_registered')} <Link to="/doctor/login" className="text-[#1E3F33] font-semibold">{t(lang,'login')}</Link>
        </div>
      </Card>
    </div>
  );
}
